import { useCallback, useLayoutEffect, useRef } from 'react';

type FocusTarget = 'selector' | 'edit';

interface UseLastServiceEditFocusArgs {
    expanded: boolean;
    selectorId: string;
    onEdit?: () => void;
    onCancel?: () => void;
}

/** Moves focus into the form after the dealer's Edit and back to Edit after Cancel; automatic
 *  expansion or collapse leaves focus where it is. */
export function useLastServiceEditFocus({
    expanded,
    selectorId,
    onEdit,
    onCancel,
}: UseLastServiceEditFocusArgs) {
    const editButtonRef = useRef<HTMLButtonElement>(null);
    const pendingTarget = useRef<FocusTarget | null>(null);

    useLayoutEffect(() => {
        const target = pendingTarget.current;
        if (!target) return;
        pendingTarget.current = null;
        if (target === 'selector' && expanded) document.getElementById(selectorId)?.focus();
        if (target === 'edit' && !expanded) editButtonRef.current?.focus();
    });

    const handleEdit = useCallback(() => {
        pendingTarget.current = 'selector';
        onEdit?.();
    }, [onEdit]);

    const handleCancel = useCallback(() => {
        pendingTarget.current = 'edit';
        onCancel?.();
    }, [onCancel]);

    return {
        editButtonRef,
        handleEdit: onEdit ? handleEdit : undefined,
        handleCancel: onCancel ? handleCancel : undefined,
    };
}
